import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import ModalWrapper from './ModalWrapper';

const INIT = {
  name: '',
  pronouns: '',
  natures: '',
  tokens: 0,
  playbook: '',
  abilities: '',
  backstory: '',
  notes: ''
};

export default function CharacterModal({ onClose, existingChar, onSaved }) {
  const [form, setForm] = useState(INIT);

  useEffect(() => {
    if (existingChar) {
      setForm({
        name: existingChar.name || '',
        pronouns: existingChar.pronouns || '',
        natures: existingChar.natures || '',
        tokens: existingChar.tokens || 0,
        playbook: existingChar.playbook || '',
        abilities: existingChar.abilities || '',
        backstory: existingChar.backstory || '',
        notes: existingChar.notes || '',
      });
    }
  }, [existingChar]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return alert('Please enter a name.');
    
    if (existingChar) {
      await supabase.from('characters').update(form).eq('id', existingChar.id);
    } else {
      await supabase.from('characters').insert([form]);
    }
    
    onSaved?.();
    onClose();
  };

  return (
    <ModalWrapper title={existingChar ? 'Edit Character' : 'New Character'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-lora text-sm font-medium text-ink-mid mb-1">Name</label>
            <input required className="form-input-styled" value={form.name} onChange={set('name')} placeholder="e.g. Bramble" />
          </div>
          <div>
            <label className="block font-lora text-sm font-medium text-ink-mid mb-1">Pronouns</label>
            <input className="form-input-styled" value={form.pronouns} onChange={set('pronouns')} placeholder="e.g. she/her" />
          </div>
        </div>

        <div>
          <label className="block font-lora text-sm font-medium text-ink-mid mb-1">Natures & Traits</label>
          <textarea className="form-input-styled min-h-[80px]" value={form.natures} onChange={set('natures')} placeholder={"Gentle\nCurious\nLikes collecting small stones"} />
          <div className="font-lora text-[0.78rem] italic text-ink-light mt-1">One per line</div>
        </div>

        <div>
          <label className="block font-lora text-sm font-medium text-ink-mid mb-1">Starting Tokens</label>
          <input type="number" min="0" max="5" className="form-input-styled w-24" value={form.tokens} onChange={(e) => setForm({...form, tokens: parseInt(e.target.value) || 0})} />
        </div>

        <div>
          <label className="block font-lora text-sm font-medium text-ink-mid mb-1">Playbook</label>
          <input className="form-input-styled" value={form.playbook} onChange={set('playbook')} placeholder="e.g. The Moth, The Rabbit, The Fox..." />
        </div>

        <div>
          <label className="block font-lora text-sm font-medium text-ink-mid mb-1">Moves & Abilities</label>
          <textarea className="form-input-styled min-h-[90px]" value={form.abilities} onChange={set('abilities')} placeholder={"List moves one per line\ne.g. Gentle Passage\nRead the Room"} />
          <div className="font-lora text-[0.78rem] italic text-ink-light mt-1">One per line</div>
        </div>

        <div>
          <label className="block font-lora text-sm font-medium text-ink-mid mb-1">Backstory</label>
          <textarea className="form-input-styled min-h-[90px]" value={form.backstory} onChange={set('backstory')} placeholder="Who are they? Where did they come from?" />
        </div>

        <div>
          <label className="block font-lora text-sm font-medium text-ink-mid mb-1">Notes</label>
          <textarea className="form-input-styled min-h-[70px]" value={form.notes} onChange={set('notes')} placeholder="Anything else worth remembering..." />
        </div>

        <div className="flex gap-3 justify-end mt-6 pt-2">
          <button type="button" onClick={onClose} className="bg-transparent border-[1.5px] border-parchment-dark rounded px-4 py-2 font-lora text-[0.84rem] text-ink-mid cursor-pointer hover:bg-parchment-dark/30">
            Cancel
          </button>
          <button type="submit" className="bg-ink text-parchment border-none rounded px-5 py-2 font-lora text-[0.84rem] cursor-pointer hover:bg-ink-mid">
            {existingChar ? 'Save Changes' : 'Add Character'}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
}